import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Home,
  FileText,
  Layout,
  BarChart3,
  ChevronLeft,
  ChevronDown,
  Menu,
  Users,
  FolderOpen,
  FileAxis3d,
  FileBarChart,
  Camera,
  Cpu,
  Images,
  Sparkles,
  MessageSquareText,
  UsersRound,
  Gauge,
  Wallet,
  Briefcase,
  ReceiptText,
  TrendingUp,
  ShieldCheck,
  MessageSquareWarning,
} from 'lucide-react';
import { useSidebar } from '@/contexts/sidebar-context';
import { useAuth } from '@/contexts/auth-hook';
import { useI18n } from '@/contexts/i18n-hook';
import { useProjeto } from '@/contexts/projeto-hook';
import { podeAcessarVendas } from '@/pages/vendas/vendas-acesso';
import { podeAcessarCarteira } from '@/pages/carteira/carteira-acesso';
import { podeAcessarEstruturacao } from '@/pages/squads/estruturacao-acesso';
import { podeAcessarIA } from '@/pages/ia/ia-acesso';

interface ItemMenu {
  path: string;
  icon: typeof Home;
  label: string;
}

interface GrupoMenu {
  label: string;
  icon: typeof Home;
  /** Prefixo de rota que mantém o grupo aberto quando alguma filha está ativa. */
  prefixo: string;
  itens: ItemMenu[];
}

function ehGrupo(entrada: ItemMenu | GrupoMenu): entrada is GrupoMenu {
  return 'itens' in entrada;
}

export function Sidebar() {
  const location = useLocation();
  const { t } = useI18n();
  const { toggleSidebar } = useSidebar();
  const { isAdmin, user } = useAuth();
  const { projeto } = useProjeto();

  const isModecProject = projeto?.nome?.toLowerCase().includes('modec');
  const isBudgetProject = projeto?.tipoProjeto === 'budget';

  // Vendas é do squad MG (o admin também enxerga). O backend faz o escopo de
  // verdade por squad; aqui só decidimos se o menu aparece.
  const vendasGrupo: GrupoMenu[] = podeAcessarVendas(user?.squadNome, isAdmin)
    ? [{
        label: t('layout.sidebar.menu.vendas'),
        icon: Briefcase,
        prefixo: '/vendas',
        itens: [{ path: '/vendas/cotacoes', icon: ReceiptText, label: t('layout.sidebar.menu.cotacoes') }],
      }]
    : [];

  // Projetos (global) e Usuários (admin) na mesma parte – sem projeto específico
  const areaGlobal = [
    { path: '/projeto-selecao', icon: FolderOpen, label: t('layout.sidebar.menu.projetos') },
    ...(isAdmin ? [
      { path: '/admin/usuarios', icon: Users, label: t('layout.sidebar.menu.usuarios') },
      { path: '/admin/feedbacks', icon: MessageSquareText, label: t('layout.sidebar.menu.feedbacks') },
    ] : []),
  ];

  // Squads no final do menu (admin)
  const squadsItem = isAdmin
    ? [{ path: '/admin/squads', icon: UsersRound, label: t('layout.sidebar.menu.squads') }]
    : [];

  // Estruturação: overview + avaliação do líder. Não é só de admin — o líder
  // precisa alcançar o formulário, e o overview atravessa projetos. Mas é
  // leitura de gestão, então nem todo squad enxerga (ver estruturacao-acesso).
  const estruturacaoItem = podeAcessarEstruturacao(user?.squadNome, isAdmin)
    ? [{ path: '/squads/overview', icon: Gauge, label: t('layout.sidebar.menu.estruturacao') }]
    : [];

  // Carteira de PIs: retrato da empresa inteira, filtrado por squad na tela.
  // Não depende de projeto, então cabe em qualquer variante do menu — mas nem
  // todo squad enxerga (ver carteira-acesso; o backend é quem barra de verdade).
  // Vira grupo para o admin, que também alcança a alocação e o custo do time.
  const carteiraItem: (ItemMenu | GrupoMenu)[] = !podeAcessarCarteira(user?.squadNome, isAdmin)
    ? []
    : isAdmin
      ? [{
          label: t('layout.sidebar.menu.carteira'),
          icon: TrendingUp,
          prefixo: '/carteira',
          itens: [
            { path: '/carteira', icon: TrendingUp, label: t('layout.sidebar.menu.carteiraPainel') },
            { path: '/carteira/equipe', icon: Users, label: t('layout.sidebar.menu.carteiraEquipe') },
          ],
        }]
      : [{ path: '/carteira', icon: TrendingUp, label: t('layout.sidebar.menu.carteira') }];

  // Assistente de IA: nem todo squad alcança (ver ia-acesso; o backend é quem
  // barra de verdade).
  const iaItem = podeAcessarIA(user?.squadNome, isAdmin)
    ? [{ path: '/ia', icon: Sparkles, label: t('layout.sidebar.menu.ia') }]
    : [];

  // Budget aparece apenas quando o projeto ativo tem controle habilitado
  const budgetItem = projeto?.budgetHabilitado
    ? [{ path: '/budget', icon: Wallet, label: t('layout.sidebar.menu.budget') }]
    : [];

  // Projeto de budget só tem a tela de Budget (sem dashboard/apreciações/etc)
  const menuItems: (ItemMenu | GrupoMenu)[] = isBudgetProject
    ? [
      ...areaGlobal,
      { path: '/budget', icon: Wallet, label: t('layout.sidebar.menu.budget') },
      ...vendasGrupo,
      ...carteiraItem,
      ...estruturacaoItem,
      ...squadsItem,
    ]
    : isModecProject
    ? [
      ...areaGlobal,
      { path: '/modec', icon: FileAxis3d, label: t('layout.sidebar.menu.mesclarPdfs') },
      ...budgetItem,
      ...vendasGrupo,
      ...carteiraItem,
      ...estruturacaoItem,
      ...squadsItem,
    ]
    : [
      ...areaGlobal,
      // A ordem abaixo acompanha o caminho do trabalho: parte do retrato do
      // projeto (dashboard), passa pelo cadastro das máquinas, pelo que foi
      // planejado e capturado em campo, e só então chega ao documento, à
      // revisão do cliente e ao fechamento (estatísticas e relatório).
      { path: '/dashboard', icon: Home, label: t('layout.sidebar.menu.dashboard') },
      { path: '/maquinas', icon: Cpu, label: t('layout.sidebar.menu.maquinas') },
      { path: '/cronograma', icon: FileBarChart, label: t('layout.sidebar.menu.cronograma') },
      { path: '/aquisicao-campo', icon: Camera, label: t('layout.sidebar.menu.aquisicaoCampo') },
      { path: '/imagens-maquinas', icon: Images, label: t('layout.sidebar.menu.imagensMaquinas') },
      { path: '/apreciacoes', icon: FileText, label: t('layout.sidebar.menu.apreciacoes') },
      { path: '/revisoes', icon: MessageSquareWarning, label: t('layout.sidebar.menu.revisoes') },
      { path: '/meus-templates', icon: Layout, label: t('layout.sidebar.menu.templates') },
      { path: '/estatisticas', icon: BarChart3, label: t('layout.sidebar.menu.estatisticas') },
      { path: '/relatorio', icon: FileBarChart, label: t('layout.sidebar.menu.relatorio') },
      ...budgetItem,
      ...vendasGrupo,
      ...iaItem,
      ...carteiraItem,
      ...estruturacaoItem,
      ...squadsItem,
    ];

  return (
    <div className="w-64 h-screen bg-schmersal-blue dark:bg-schmersal-gray-darker flex flex-col">
      {/* Logo/Brand */}
      <div className="px-5 py-4 flex items-center justify-between border-b border-white/15 dark:border-schmersal-gray-dark">
        <div className="flex items-center space-x-2.5 min-w-0">
          <div className="h-7 w-7 flex-none rounded-md bg-schmersal-turquoise flex items-center justify-center">
            <ShieldCheck className="h-4 w-4 text-schmersal-blue" />
          </div>
          <h1 className="text-[15px] font-bold text-white truncate tracking-tight">{t('layout.sidebar.titulo')}</h1>
        </div>
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            toggleSidebar();
          }}
          type="button"
          className="p-1.5 rounded-lg text-schmersal-gray-light dark:text-white
                   hover:text-white dark:hover:text-schmersal-yellow hover:bg-schmersal-blue-light dark:hover:bg-schmersal-blue
                   transition-colors duration-200 flex-shrink-0"
          title={t('layout.sidebar.ocultar')}
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
      </div>

      {/* Navigation */}
      <nav className="mt-4 flex-1 px-3 overflow-y-auto">
        {menuItems.map((entrada) => {
          if (ehGrupo(entrada)) {
            return (
              <GrupoSidebar
                key={entrada.prefixo}
                grupo={entrada}
                caminhoAtual={location.pathname}
              />
            );
          }

          const Icon = entrada.icon;
          const isActive = location.pathname === entrada.path ||
            (entrada.path !== '/' && location.pathname.startsWith(entrada.path));

          return (
            <Link
              key={entrada.path}
              to={entrada.path}
              className={`flex items-center px-3 py-2.5 mb-0.5 text-[13.5px] rounded-lg
                        transition-all duration-200 ${isActive
                  ? 'bg-white/[0.14] dark:bg-schmersal-blue text-white font-semibold shadow-[inset_2px_0_0_theme(colors.schmersal.turquoise.DEFAULT)]'
                  : 'text-white/70 dark:text-white font-medium hover:bg-white/10 dark:hover:bg-schmersal-blue hover:text-white dark:hover:text-schmersal-yellow'
                }`}
            >
              <Icon className="mr-2.5 h-[17px] w-[17px] flex-none" />
              {entrada.label}
            </Link>
          );
        })}
      </nav>

      {/* Footer */}
      <div className="px-5 py-3.5 border-t border-white/15 dark:border-schmersal-gray-dark">
        <div className="text-[11px] text-white/55 dark:text-schmersal-gray-medium text-right">
          <p className="font-medium">{t('layout.sidebar.rodape.area')}</p>
          <p className="mt-0.5">{t('layout.sidebar.rodape.normas')}</p>
        </div>
      </div>
    </div>
  );
}

/**
 * Item de menu com filhas (Vendas → Cotações).
 *
 * Abre sozinho quando a rota atual está dentro do grupo — assim quem entra por
 * link direto não vê o menu fechado sobre a própria tela — e a pessoa pode
 * abrir/fechar manualmente a partir daí.
 */
function GrupoSidebar({ grupo, caminhoAtual }: { grupo: GrupoMenu; caminhoAtual: string }) {
  const dentroDoGrupo = caminhoAtual.startsWith(grupo.prefixo);
  const [aberto, setAberto] = React.useState(dentroDoGrupo);

  React.useEffect(() => {
    if (dentroDoGrupo) setAberto(true);
  }, [dentroDoGrupo]);

  const Icon = grupo.icon;

  return (
    <div className="mb-1">
      <button
        type="button"
        onClick={() => setAberto((v) => !v)}
        aria-expanded={aberto}
        className={`w-full flex items-center px-3 py-2.5 text-[13.5px] rounded-lg
                    transition-all duration-200 ${dentroDoGrupo
            ? 'bg-white/10 dark:bg-schmersal-blue/60 text-white font-semibold'
            : 'text-white/70 dark:text-white font-medium hover:bg-white/10 dark:hover:bg-schmersal-blue hover:text-white dark:hover:text-schmersal-yellow'
          }`}
      >
        <Icon className="mr-2.5 h-[17px] w-[17px] flex-none" />
        <span className="flex-1 text-left">{grupo.label}</span>
        <ChevronDown className={`h-4 w-4 transition-transform ${aberto ? '' : '-rotate-90'}`} />
      </button>

      {aberto && (
        <div className="mt-1 ml-4 pl-3 border-l border-white/15 space-y-1">
          {grupo.itens.map((item) => {
            const ItemIcon = item.icon;
            // Só o caminho mais específico acende: com "/carteira" e
            // "/carteira/equipe" no mesmo grupo, o prefixo simples marcaria os dois.
            const combina = (p: string) => caminhoAtual === p || caminhoAtual.startsWith(`${p}/`);
            const maisEspecifico = grupo.itens
              .filter((i) => combina(i.path))
              .sort((a, b) => b.path.length - a.path.length)[0];
            const ativo = maisEspecifico?.path === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center px-3 py-2 text-[13px] rounded-lg transition-all duration-200 ${ativo
                    ? 'bg-white/[0.14] dark:bg-schmersal-blue text-white font-semibold'
                    : 'text-white/70 dark:text-white font-medium hover:bg-white/10 dark:hover:bg-schmersal-blue hover:text-white dark:hover:text-schmersal-yellow'
                  }`}
              >
                <ItemIcon className="mr-2 h-4 w-4 flex-none" />
                {item.label}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Botão para mostrar o sidebar quando estiver oculto
export function SidebarToggle() {
  const { t } = useI18n();
  const { isCollapsed, toggleSidebar } = useSidebar();
  const buttonRef = React.useRef<HTMLButtonElement>(null);

  const handleClick = React.useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.nativeEvent) {
      e.nativeEvent.stopImmediatePropagation();
    }
    // Forçar o foco no botão antes de alternar
    if (buttonRef.current) {
      buttonRef.current.blur();
    }
    toggleSidebar();
  }, [toggleSidebar]);

  if (!isCollapsed) return null;

  return (
    <button
      ref={buttonRef}
      onClick={handleClick}
      onMouseDown={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      type="button"
      className="fixed left-0 top-1/2 -translate-y-1/2 z-50 
               hidden md:block
               p-3 bg-schmersal-blue dark:bg-schmersal-gray-darker text-white rounded-r-lg shadow-xl 
               hover:bg-schmersal-blue-light dark:hover:bg-schmersal-blue transition-all duration-200
               border-r-2 border-white dark:border-schmersal-gray-darkest"
      title={t('layout.sidebar.mostrar')}
    >
      <Menu className="h-5 w-5" />
    </button>
  );
}
